import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../lib/utils';
import {
  Wrench, Plus, Edit2, Trash2, AlertTriangle, Clock, ShieldAlert, CalendarClock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ExportButton from '../components/ExportButton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  scheduled: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
};

const emptyForm = {
  asset_id: '', issue_reported: '', date_reported: format(new Date(), 'yyyy-MM-dd'),
  technician: '', repair_cost: '', parts_replaced: '',
  next_maintenance_date: '', status: 'scheduled', completion_date: '', notes: '',
};

const MaintenancePage = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['admin', 'finance_officer', 'project_manager', 'school_head']);

  const [records, setRecords] = useState([]);
  const [assets, setAssets] = useState([]);
  const [alerts, setAlerts] = useState({ upcoming: [], overdue: [], expiring_warranties: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [r, a, alt] = await Promise.all([
        axios.get(`${API_URL}/api/maintenance`),
        axios.get(`${API_URL}/api/assets`),
        axios.get(`${API_URL}/api/maintenance/alerts`),
      ]);
      setRecords(r.data);
      setAssets(a.data);
      setAlerts(alt.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load maintenance data'); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm }); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...emptyForm, ...r, repair_cost: r.repair_cost ?? '' }); setDialogOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, repair_cost: form.repair_cost ? parseFloat(form.repair_cost) : 0 };
      if (editing) {
        await axios.put(`${API_URL}/api/maintenance/${editing.id}`, payload);
        toast.success('Record updated');
      } else {
        await axios.post(`${API_URL}/api/maintenance`, payload);
        toast.success('Maintenance recorded');
      }
      setDialogOpen(false); setEditing(null); setForm({ ...emptyForm });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const deleteRecord = async (id) => {
    if (!window.confirm('Delete this maintenance record?')) return;
    try { await axios.delete(`${API_URL}/api/maintenance/${id}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };

  const totalCost = records.reduce((s, r) => s + (parseFloat(r.repair_cost) || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold">Maintenance Management</h2>
          <p className="text-muted-foreground">Service history, repairs and maintenance schedules</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <ExportButton path="exports/list/maintenance" filenameBase="suubi_maintenance" testId="maintenance-export-btn" />
            <Button onClick={openCreate} data-testid="add-maintenance-btn">
              <Plus className="mr-1 h-4 w-4" /> Log Maintenance
            </Button>
          </div>
        )}
        {!canEdit && (
          <ExportButton path="exports/list/maintenance" filenameBase="suubi_maintenance" />
        )}
      </div>

      {/* Alert Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-amber-200">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-100"><Clock className="h-5 w-5 text-amber-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Upcoming (30 days)</p>
              <p className="text-2xl font-heading font-bold text-amber-600">{alerts.upcoming.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-red-100"><AlertTriangle className="h-5 w-5 text-red-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Overdue Repairs</p>
              <p className="text-2xl font-heading font-bold text-red-600">{alerts.overdue.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-orange-100"><ShieldAlert className="h-5 w-5 text-orange-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Warranties Expiring</p>
              <p className="text-2xl font-heading font-bold text-orange-600">{alerts.expiring_warranties.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-primary/20">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10"><Wrench className="h-5 w-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Total Repair Cost</p>
              <p className="text-2xl font-heading font-bold text-primary">{formatCurrency(totalCost)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="records">
        <TabsList>
          <TabsTrigger value="records" data-testid="tab-maintenance-records">Service Records ({records.length})</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-maintenance-alerts">Alerts</TabsTrigger>
        </TabsList>

        <TabsContent value="records" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date Reported</TableHead>
                    <TableHead>Asset</TableHead>
                    <TableHead>Issue</TableHead>
                    <TableHead>Technician</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead>Next Service</TableHead>
                    <TableHead>Status</TableHead>
                    {canEdit && <TableHead className="w-24"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-10">Loading...</TableCell></TableRow>
                  ) : records.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No maintenance records yet</TableCell></TableRow>
                  ) : records.map((r) => {
                    const asset = assets.find(a => a.id === r.asset_id);
                    return (
                      <TableRow key={r.id} className="table-row-hover" data-testid={`maintenance-row-${r.id}`}>
                        <TableCell>{r.date_reported}</TableCell>
                        <TableCell><div><p className="font-medium">{asset?.name || r.asset_name || '—'}</p>{asset && <p className="text-xs font-mono text-muted-foreground">{asset.asset_id}</p>}</div></TableCell>
                        <TableCell className="max-w-xs truncate">{r.issue_reported}</TableCell>
                        <TableCell>{r.technician || '—'}</TableCell>
                        <TableCell className="text-right">{r.repair_cost ? formatCurrency(r.repair_cost) : '—'}</TableCell>
                        <TableCell className="text-sm">{r.next_maintenance_date || '—'}</TableCell>
                        <TableCell><Badge className={STATUS_STYLES[r.status] || ''}>{r.status?.replace(/_/g, ' ')}</Badge></TableCell>
                        {canEdit && (
                          <TableCell>
                            <div className="flex gap-1 justify-end">
                              <Button size="icon" variant="ghost" onClick={() => openEdit(r)}><Edit2 className="h-4 w-4" /></Button>
                              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteRecord(r.id)}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="mt-4 space-y-4">
          {/* Overdue */}
          {alerts.overdue.length > 0 && (
            <Card className="border-red-200">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600" /> Overdue Repairs</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {alerts.overdue.map(o => (
                  <div key={o.id} className="flex justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/10">
                    <div>
                      <p className="font-medium">{o.asset_name || 'Unknown Asset'}</p>
                      <p className="text-xs text-muted-foreground">Next service: {o.next_maintenance_date}</p>
                    </div>
                    <Badge className="bg-red-100 text-red-700">{o.days_overdue} days overdue</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Upcoming */}
          {alerts.upcoming.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4 text-amber-600" /> Upcoming Maintenance (30 days)</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {alerts.upcoming.map(u => (
                  <div key={u.id} className="flex justify-between p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10">
                    <div>
                      <p className="font-medium">{u.asset_name || 'Unknown Asset'}</p>
                      <p className="text-xs text-muted-foreground">Scheduled: {u.next_maintenance_date}</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">in {u.days_until} days</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Warranty */}
          {alerts.expiring_warranties.length > 0 && (
            <Card className="border-orange-200">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-orange-600" /> Warranties Expiring (60 days)</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {alerts.expiring_warranties.map(w => (
                  <div key={w.asset_id} className="flex justify-between p-3 rounded-lg bg-orange-50 dark:bg-orange-900/10">
                    <div>
                      <p className="font-medium">{w.name}</p>
                      <p className="text-xs text-muted-foreground">Expires: {w.warranty_expiry}</p>
                    </div>
                    <Badge className="bg-orange-100 text-orange-700">{w.days_until} days</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {alerts.overdue.length + alerts.upcoming.length + alerts.expiring_warranties.length === 0 && (
            <Card><CardContent className="p-12 text-center text-muted-foreground">No active alerts. All assets are up-to-date.</CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Maintenance Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setForm({ ...emptyForm }); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit Maintenance' : 'Log Maintenance'}</DialogTitle>
            <DialogDescription>Record a service event for an asset</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Asset *</Label>
              <Select value={form.asset_id} onValueChange={(v) => setForm({ ...form, asset_id: v })}>
                <SelectTrigger data-testid="maintenance-asset-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{assets.map(a => <SelectItem key={a.id} value={a.id}>{a.asset_id} · {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Issue Reported *</Label><Textarea rows={2} value={form.issue_reported} onChange={(e) => setForm({ ...form, issue_reported: e.target.value })} required data-testid="maintenance-issue-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date Reported *</Label><Input type="date" value={form.date_reported} onChange={(e) => setForm({ ...form, date_reported: e.target.value })} required /></div>
              <div className="space-y-1.5"><Label>Technician</Label><Input value={form.technician} onChange={(e) => setForm({ ...form, technician: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Repair Cost</Label><Input type="number" value={form.repair_cost} onChange={(e) => setForm({ ...form, repair_cost: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Next Maintenance Date</Label><Input type="date" value={form.next_maintenance_date} onChange={(e) => setForm({ ...form, next_maintenance_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Completion Date</Label><Input type="date" value={form.completion_date} onChange={(e) => setForm({ ...form, completion_date: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Parts Replaced</Label><Textarea rows={2} value={form.parts_replaced} onChange={(e) => setForm({ ...form, parts_replaced: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="maintenance-submit-btn">{editing ? 'Update' : 'Save'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaintenancePage;
